import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import Select from 'react-select';
import queryString from 'query-string';
import { programRequestsApi } from '../api/programRequestsApi';
import { RadioCheckGroup } from 'components/academic-affair-report/components/RadioCheckGroup';
import { InstitutionField } from '../shared/InstitutionField';
import { ProgramSelector } from '../shared/ProgramSelector';
import {
    AdditionalCommentsField,
    commentsLengthError,
} from '../shared/AdditionalCommentsField';
import { createChangeDetailsModel } from './changeDetailsModel';
import { createChangeDetailsFlow } from './createChangeDetailsFlow';

const DELIVERY_MODE_OPTIONS = [
    { value: 1, label: 'On Campus Only' },
    { value: 2, label: 'Online Only' },
    { value: 3, label: 'On Campus & Online' },
];

const ONLINE_NATURE_OPTIONS = [
    { value: 1, label: '100% Online' },
    { value: 2, label: 'Blended' },
];

const LOCATION_STATUS_OPTIONS = [
    { value: 1, label: 'Active' },
    { value: 3, label: 'Suspended' },
    { value: 4, label: 'Eliminated' },
];

const normalizeNumber = value =>
    value === '' || value === null || value === undefined ? null : Number(value);

const creditHoursError = form => {
    const minimum = normalizeNumber(form.creditMin);
    const maximum = normalizeNumber(form.creditMax);
    if (minimum === null || maximum === null) {
        return 'Enter both Minimum and Maximum credit hours.';
    }
    if (minimum <= 0 || maximum <= 0) {
        return 'Credit Hours Minimum and Maximum must be greater than 0.';
    }
    if (minimum > maximum) {
        return 'Credit Hours Maximum must be greater than or equal to Minimum.';
    }
    return null;
};

const locationSnapshot = locations =>
    (locations ?? [])
        .map(location => `${location.value}:${location.statusId}`)
        .sort()
        .join('|');

const hasChanges = (form, currentProgram) => {
    if (!currentProgram) return false;

    return (
        form.programName.trim().toLowerCase() !==
        (currentProgram.programFormalName ?? '').trim().toLowerCase() ||
        form.cipCode !== currentProgram.cipSixDigitCode ||
        form.degreeLevel !== currentProgram.degreeLevelCode ||
        normalizeNumber(form.creditMin) !== currentProgram.programCreditReqMin ||
        normalizeNumber(form.creditMax) !== currentProgram.programCreditReqMax ||
        Number(form.deliveryMode) !== Number(currentProgram.programDistanceEdId ?? 0) ||
        Number(form.onlineNature ?? 0) !==
        Number(currentProgram.programDistanceEdOnlineId ?? 0) ||
        locationSnapshot(form.locations) !==
        locationSnapshot(
            (currentProgram.programLocation ?? []).map(location => ({
                value: location.locationId,
                statusId: location.statusId,
            }))
        )
    );
};

export const ChangeDetailsForm = ({
    onBack,
    onSuccess,
    institutionOptions: parentInstitutionOptions = [],
    programsByInstitution = {},
    maximumAdditionalCommentsLength,
    initialModel,
    submitFlow = createChangeDetailsFlow,
    mode = 'create',
    requestDetails,
}) => {
    const [form, setForm] = useState(
        () => initialModel ?? createChangeDetailsModel()
    );
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [cipOptions, setCipOptions] = useState([]);
    const [degreeLevelOptions, setDegreeLevelOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [loadingPrograms, setLoadingPrograms] = useState(false);
    const [initializingEdit, setInitializingEdit] = useState(mode === 'edit');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (parentInstitutionOptions.length > 0) {
            setInstitutionOptions(parentInstitutionOptions);
        } else {
            programRequestsApi.getInstitutions().then(response => {
                setInstitutionOptions(
                    (response ?? []).map(item => ({
                        value: item.label,
                        label: item.value,
                    }))
                );
            });
        }

        programRequestsApi.getCipCodes().then(response => {
            setCipOptions(
                (response ?? []).map(item => ({
                    value: item.cipCode,
                    label: `${item.cipCode} - ${item.cipName}`,
                }))
            );
        });

        programRequestsApi.getDegreeLevels().then(response => {
            setDegreeLevelOptions(
                (response ?? []).map(item => ({
                    value: item.label,
                    label: item.value,
                }))
            );
        });
    }, []);

    useEffect(() => {
        if (institutionOptions.length === 1 && form.institutionId === null) {
            setForm(current => ({
                ...current,
                institutionId: institutionOptions[0].value,
            }));
        }
    }, [institutionOptions]);

    useEffect(() => {
        if (!form.institutionId) {
            setPrograms([]);
            setLocationOptions([]);
            setForm(current => ({
                ...createChangeDetailsModel(),
                institutionId: current.institutionId,
            }));
            setInitializingEdit(false);
            return;
        }

        if (mode !== 'edit') {
            setForm(current => ({
                ...createChangeDetailsModel(),
                institutionId: current.institutionId,
            }));
        }

        const cachedPrograms = programsByInstitution[form.institutionId];
        if (Array.isArray(cachedPrograms) && cachedPrograms.length > 0) {
            setPrograms(cachedPrograms);
            setInitializingEdit(false);
        } else {
            setLoadingPrograms(true);
            const programQuery = queryString.stringify(
                { institutions: form.institutionId },
                { arrayFormat: 'separator', skipEmptyString: true }
            );
            programRequestsApi
                .getProgramsForInstitution(programQuery)
                .then(response => setPrograms(response?.programs?.flat() ?? []))
                .finally(() => {
                    setLoadingPrograms(false);
                    setInitializingEdit(false);
                });
        }

        const locationQuery = queryString.stringify(
            { institutions: form.institutionId },
            { arrayFormat: 'separator', skipEmptyString: true }
        );
        programRequestsApi.getLocations(locationQuery).then(response => {
            setLocationOptions(
                (response?.locations ?? []).map(item => ({
                    value: item.key,
                    label: item.value,
                }))
            );
        });
    }, [form.institutionId, mode]);

    const currentProgram =
        programs.find(program => program.programId === form.programId) ?? null;

    const setField = (key, value) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    const handleProgramChange = programId => {
        const selected = programs.find(program => program.programId === programId);
        if (!selected) {
            setForm(current => ({
                ...createChangeDetailsModel(),
                institutionId: current.institutionId,
            }));
            return;
        }

        setForm(current => ({
            ...current,
            programId,
            programName: selected.programFormalName ?? '',
            cipCode: selected.cipSixDigitCode ?? null,
            degreeLevel: selected.degreeLevelCode ?? null,
            creditMin: selected.programCreditReqMin ?? '',
            creditMax: selected.programCreditReqMax ?? '',
            deliveryMode: selected.programDistanceEdId ?? null,
            onlineNature: selected.programDistanceEdOnlineId ?? 0,
            locations: (selected.programLocation ?? []).map(location => ({
                value: location.locationId,
                label: location.locationName ?? location.locationId,
                statusId: location.statusId,
                isNew: false,
            })),
            comments: '',
        }));
        setSubmitted(false);
        setSubmitError(null);
    };

    const handleDeliveryModeChange = values => {
        const deliveryMode = values.deliveryMode === '' ? null : values.deliveryMode;
        setForm(current => ({
            ...current,
            deliveryMode,
            onlineNature:
                deliveryMode === 1 ? 0 :
                    deliveryMode === 2 ? 1 :
                        current.onlineNature === 0 ? null : current.onlineNature,
        }));
    };

    const handleOnlineNatureChange = values => {
        setField(
            'onlineNature',
            values.onlineNature === '' ? null : values.onlineNature
        );
    };

    const updateLocationStatus = (locationId, statusId) => {
        setForm(current => ({
            ...current,
            locations: current.locations.map(location =>
                location.value === locationId
                    ? { ...location, statusId: Number(statusId) }
                    : location
            ),
        }));
    };

    const addLocation = option => {
        if (!option) return;
        setForm(current => ({
            ...current,
            locations: [
                ...current.locations,
                { ...option, statusId: 1, isNew: true },
            ],
        }));
    };

    const removeNewLocation = locationId => {
        setForm(current => ({
            ...current,
            locations: current.locations.filter(
                location => location.value !== locationId
            ),
        }));
    };

    const validationError = () => {
        if (!form.institutionId) return 'Institution is required.';
        if (!form.programId) return 'Program is required.';
        if (!form.programName.trim()) return 'Program Name cannot be empty.';
        if (!form.cipCode) return 'CIP Code cannot be empty.';
        if (!form.degreeLevel) return 'Degree / Award Level cannot be empty.';
        if (!form.deliveryMode) return 'Delivery Mode cannot be empty.';
        if (form.deliveryMode === 3 && !form.onlineNature) {
            return 'Nature of Online Program is required.';
        }
        if (!form.locations.length) return 'At least one location offering is required.';
        if (creditHoursError(form)) return creditHoursError(form);
        if (commentsLengthError(form.comments, maximumAdditionalCommentsLength)) {
            return commentsLengthError(
                form.comments,
                maximumAdditionalCommentsLength
            );
        }
        if (!hasChanges(form, currentProgram)) {
            return "At least one field must differ from the program's current value.";
        }
        return null;
    };

    const handleSubmit = () => {
        setSubmitted(true);
        setSubmitError(null);
        if (validationError()) return;

        setSubmitting(true);
        const onError = error => {
            setSubmitError(
                error?.response?.data?.message ??
                'An unexpected error occurred. Please try again.'
            );
            setSubmitting(false);
        };

        submitFlow({ form, details: requestDetails, onError })
            .then(result => {
                setSubmitting(false);
                if (!result) return;
                onSuccess();
            });
    };

    const currentCipLabel =
        cipOptions.find(option => option.value === currentProgram?.cipSixDigitCode)
            ?.label ?? currentProgram?.cipSixDigitCode;
    const currentDegreeLabel =
        degreeLevelOptions.find(
            option => option.value === currentProgram?.degreeLevelCode
        )?.label ?? currentProgram?.degreeLevelCode;
    const currentDeliveryLabel =
        DELIVERY_MODE_OPTIONS.find(
            option => option.value === currentProgram?.programDistanceEdId
        )?.label ?? 'None';
    const currentNatureLabel =
        currentProgram?.programDistanceEdOnlineId === 0
            ? 'None'
            : ONLINE_NATURE_OPTIONS.find(
                option =>
                    option.value === currentProgram?.programDistanceEdOnlineId
            )?.label ?? 'None';
    const availableLocationOptions = locationOptions.filter(
        option => !form.locations.some(location => location.value === option.value)
    );
    const error = submitted ? validationError() : null;

    if (mode === 'edit' && initializingEdit) {
        return (
            <p className="text-muted small text-center py-4 mb-0">
                Loading request details…
            </p>
        );
    }

    return (
        <div>
            {mode !== 'edit' && (
                <button type="button" className="btn btn-link p-0 mb-3" onClick={onBack}>
                    ← Back
                </button>
            )}

            <InstitutionField
                institutionOptions={institutionOptions}
                value={form.institutionId}
                onChange={value => setField('institutionId', value)}
            />

            {loadingPrograms && <p className="small text-muted">Loading programs…</p>}
            <ProgramSelector
                inputId="change-details-program"
                programs={programs}
                value={form.programId}
                onChange={handleProgramChange}
            />

            {currentProgram && (
                <>
                    <hr />

                    <FormGroup>
                        <Label for="change-details-name">Program Name</Label>
                        <div className="small text-muted mb-1">
                            Current: {currentProgram.programFormalName}
                        </div>
                        <Input
                            id="change-details-name"
                            value={form.programName}
                            onChange={event =>
                                setField('programName', event.target.value)
                            }
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="change-details-cip">CIP Code</Label>
                        <div className="small text-muted mb-1">
                            Current: {currentCipLabel}
                        </div>
                        <Select
                            inputId="change-details-cip"
                            options={cipOptions}
                            value={
                                cipOptions.find(
                                    option => option.value === form.cipCode
                                ) ?? null
                            }
                            onChange={option =>
                                setField('cipCode', option?.value ?? null)
                            }
                            placeholder="Search CIP codes..."
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="change-details-degree">
                            Degree / Award Level
                        </Label>
                        <div className="small text-muted mb-1">
                            Current: {currentDegreeLabel}
                        </div>
                        <Select
                            inputId="change-details-degree"
                            options={degreeLevelOptions}
                            value={
                                degreeLevelOptions.find(
                                    option => option.value === form.degreeLevel
                                ) ?? null
                            }
                            onChange={option =>
                                setField('degreeLevel', option?.value ?? null)
                            }
                            placeholder="Select degree level..."
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Credit Hours</Label>
                        <div className="small text-muted mb-1">
                            Current: Min {currentProgram.programCreditReqMin ?? 'None'}
                            {' — '}
                            Max {currentProgram.programCreditReqMax ?? 'None'}
                        </div>
                        <div className="d-flex" style={{ gap: '1rem' }}>
                            <Input
                                type="number"
                                aria-label="Minimum credit hours"
                                placeholder="Min"
                                min="0"
                                value={form.creditMin}
                                onChange={event =>
                                    setField('creditMin', event.target.value)
                                }
                            />
                            <Input
                                type="number"
                                aria-label="Maximum credit hours"
                                placeholder="Max"
                                min="0"
                                value={form.creditMax}
                                onChange={event =>
                                    setField('creditMax', event.target.value)
                                }
                            />
                        </div>
                        {submitted && creditHoursError(form) && (
                            <p className="text-danger mt-1">
                                {creditHoursError(form)}
                            </p>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label className="d-block">Delivery Mode</Label>
                        <div className="small text-muted mb-1">
                            Current: {currentDeliveryLabel}
                        </div>
                        <RadioCheckGroup
                            name="deliveryMode"
                            value={form.deliveryMode}
                            options={DELIVERY_MODE_OPTIONS}
                            onChange={handleDeliveryModeChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label className="d-block">Nature of Online Program</Label>
                        <div className="small text-muted mb-1">
                            Current: {currentNatureLabel}
                        </div>
                        {form.deliveryMode === 1 ? (
                            <div className="form-control-plaintext">None</div>
                        ) : form.deliveryMode === 2 ? (
                            <div className="form-control-plaintext">100% Online</div>
                        ) : (
                            <RadioCheckGroup
                                name="onlineNature"
                                value={form.onlineNature}
                                options={ONLINE_NATURE_OPTIONS}
                                onChange={handleOnlineNatureChange}
                            />
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>Location Offerings</Label>
                        <div className="border rounded p-2 mb-2">
                            {form.locations.map(location => (
                                <div
                                    key={location.value}
                                    className="d-flex align-items-center mb-2"
                                    style={{ gap: '0.75rem' }}
                                >
                                    <span className="flex-grow-1">{location.label}</span>
                                    <Input
                                        type="select"
                                        value={location.statusId}
                                        onChange={event =>
                                            updateLocationStatus(
                                                location.value,
                                                event.target.value
                                            )
                                        }
                                        style={{ width: '10rem' }}
                                    >
                                        {LOCATION_STATUS_OPTIONS.map(option => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </Input>
                                    {location.isNew && (
                                        <Button
                                            type="button"
                                            color="link"
                                            className="text-danger p-0"
                                            onClick={() =>
                                                removeNewLocation(location.value)
                                            }
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <Select
                            options={availableLocationOptions}
                            value={null}
                            onChange={addLocation}
                            placeholder="+ Add Location to Program"
                            isClearable={false}
                        />
                    </FormGroup>

                    <AdditionalCommentsField
                        id="change-details-comments"
                        value={form.comments}
                        onChange={value => setField('comments', value)}
                        maximumLength={maximumAdditionalCommentsLength}
                        submitted={submitted}
                    />
                </>
            )}

            {error && !creditHoursError(form) && (
                <div className="alert alert-danger mt-3" role="alert">
                    {error}
                </div>
            )}
            {submitError && (
                <div className="alert alert-danger mt-3" role="alert">
                    {submitError}
                </div>
            )}

            <div className="d-flex justify-content-end mt-4">
                <Button
                    color="secondary"
                    className="mr-2"
                    onClick={onBack}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting || !currentProgram}
                >
                        {submitting
                            ? mode === 'edit' ? 'Saving…' : 'Submitting…'
                            : mode === 'edit' ? 'Save Changes' : 'Submit Request'}
                </Button>
            </div>
        </div>
    );
};
